// Servicio de Bookmarks para DevNews
// Maneja favoritos e historial de lectura en localStorage y Firestore
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { trackEvent } from '../plugins/analytics';

const STORAGE_KEY = 'devnews_bookmarks';
const MAX_HISTORY_ITEMS = 100;

// Parámetros GA4 comunes para un item del feed (registrarlos como dimensiones personalizadas en GA)
function itemParams(item) {
  return {
    content_type: item.sourceType,
    item_id: item.id || item.link,
    item_name: item.title,
    source: item.sourceTitle,
    category: item.sourceCategory || item.category,
  };
}

class BookmarksService {
  constructor() {
    this.data = this.loadLocal();
    this.unsubscribeAuth = null;

    // Iniciar escucha de auth para sincronizar
    // Esto se llama dinámicamente o podríamos importarlo, pero para evitar ciclos
    // dejaremos que el main o sidebar inicie la sincronización si es necesario, 
    // o lo hacemos aquí si auth ya está inicializado.
  }

  /**
   * Cargar datos desde localStorage
   */
  loadLocal() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : { favorites: [], history: [] };
    } catch (e) {
      console.error('Error loading bookmarks:', e);
      return { favorites: [], history: [] };
    }
  }

  /**
   * Guardar datos en localStorage y Cloud si está logueado
   */
  async save() {
    try {
      // 1. Guardar local
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));

      // 2. Emitir evento
      window.dispatchEvent(new Event('bookmarks-updated'));

      // 3. Guardar en Cloud si hay usuario
      if (auth.currentUser) {
        await this.saveToCloud(auth.currentUser.uid);
      }
    } catch (e) {
      console.error('Error saving bookmarks:', e);
    }
  }

  /**
   * Guardar en Firestore
   */
  async saveToCloud(uid) {
    try {
      const userRef = doc(db, 'users', uid);
      const user = auth.currentUser;

      const payload = {
        ...this.data,
        updatedAt: new Date().toISOString()
      };

      // Agregar metadatos del usuario si están disponibles
      if (user) {
        payload.profile = {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        };
      }

      await setDoc(userRef, payload, { merge: true });
      console.log('☁️ Sincronizado con la nube');
    } catch (e) {
      console.error('Error syncing to cloud:', e);
    }
  }

  /**
   * Fusionar datos de la nube
   */
  async syncUser(user) {
    if (!user) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        const cloudData = docSnap.data();

        // Estrategia de mezcla: 
        // Combinamos listas evitando duplicados por ID/Link
        // Priorizamos la nube si hay conflicto, pero aquí solo sumamos

        const mergedFavorites = this.mergeLists(cloudData.favorites || [], this.data.favorites);
        const mergedHistory = this.mergeLists(cloudData.history || [], this.data.history);

        // Actualizamos estado local
        this.data.favorites = mergedFavorites;
        this.data.history = mergedHistory; // El historial podría recortarse

        // Guardamos la versión fusionada
        this.save();

        console.log('☁️ Datos recuperados y fusionados de la nube');
      } else {
        // Usuario nuevo en nube, subir lo local
        await this.saveToCloud(user.uid);
      }
    } catch (e) {
      console.error('Error syncing user:', e);
    }
  }

  /**
   * Helper para fusionar listas de items (favoritos/historial)
   */
  mergeLists(cloudList, localList) {
    // Usamos un Map por ID para unicidad
    const map = new Map();

    // Primero cloud
    cloudList.forEach(item => {
      const id = item.id || item.link;
      map.set(id, item);
    });

    // Luego local (sobreescribe si es más reciente? o preservamos cloud? 
    // Asumiremos que queremos mantener todo. Si ya existe, nos quedamos con el que estaba (cloud))
    localList.forEach(item => {
      const id = item.id || item.link;
      if (!map.has(id)) {
        map.set(id, item);
      }
    });

    return Array.from(map.values());
  }

  // ==================== FAVORITOS ====================

  /**
   * Normalizar item para guardado (convertir Date a ISO string)
   */
  normalizeItem(item) {
    const normalized = { ...item };

    // Convertir pubDate a ISO string si es un objeto Date
    if (normalized.pubDate) {
      if (normalized.pubDate instanceof Date) {
        normalized.pubDate = normalized.pubDate.toISOString();
      } else if (typeof normalized.pubDate === 'object') {
        // Si es un objeto Date serializado, intentar convertirlo
        try {
          normalized.pubDate = new Date(normalized.pubDate).toISOString();
        } catch (e) {
          console.warn('No se pudo convertir pubDate:', e);
        }
      }
    }

    return normalized;
  }

  /**
   * Agregar item a favoritos
   */
  addToFavorites(item) {
    const id = item.id || item.link;
    if (!this.isFavorite(id)) {
      this.data.favorites.unshift({
        ...this.normalizeItem(item),
        addedAt: Date.now()
      });
      this.save();
      trackEvent('add_favorite', itemParams(item));
      console.log('✨ Agregado a favoritos:', item.title);
      return true;
    }
    return false;
  }

  /**
   * Quitar item de favoritos
   */
  removeFromFavorites(itemId) {
    const initialLength = this.data.favorites.length;
    this.data.favorites = this.data.favorites.filter(
      item => (item.id || item.link) !== itemId
    );

    if (this.data.favorites.length < initialLength) {
      this.save();
      console.log('🗑️ Eliminado de favoritos:', itemId);
      return true;
    }
    return false;
  }

  /**
   * Obtener todos los favoritos
   */
  getFavorites() {
    return this.data.favorites;
  }

  /**
   * Verificar si un item está en favoritos
   */
  isFavorite(itemId) {
    return this.data.favorites.some(
      item => (item.id || item.link) === itemId
    );
  }

  /**
   * Contar favoritos
   */
  getFavoritesCount() {
    return this.data.favorites.length;
  }

  // ==================== HISTORIAL ====================

  /**
   * Agregar item al historial (cuando se lee)
   */
  addToHistory(item) {
    const id = item.id || item.link;
    // Todas las tarjetas (noticia, paper, video) y el swipe de Shorts pasan por aquí al abrir contenido
    trackEvent('select_content', itemParams(item));

    // Eliminar si ya existe (para moverlo al inicio con nueva fecha)
    this.data.history = this.data.history.filter(
      item => (item.id || item.link) !== id
    );

    // Agregar al inicio con timestamp
    this.data.history.unshift({
      ...this.normalizeItem(item),
      readAt: Date.now()
    });

    // Limitar tamaño del historial
    if (this.data.history.length > MAX_HISTORY_ITEMS) {
      this.data.history = this.data.history.slice(0, MAX_HISTORY_ITEMS);
    }

    this.save();
    console.log('📖 Agregado al historial:', item.title);
    return true;
  }

  /**
   * Quitar item específico del historial
   */
  removeFromHistory(itemId) {
    const initialLength = this.data.history.length;
    this.data.history = this.data.history.filter(
      item => (item.id || item.link) !== itemId
    );

    if (this.data.history.length < initialLength) {
      this.save();
      console.log('🗑️ Eliminado del historial:', itemId);
      return true;
    }
    return false;
  }

  /**
   * Limpiar todo el historial
   */
  clearHistory() {
    this.data.history = [];
    this.save();
    console.log('🧹 Historial limpiado');
    return true;
  }

  /**
   * Obtener todo el historial (ordenado por más reciente)
   */
  getHistory() {
    return this.data.history;
  }

  /**
   * Verificar si un item está en el historial
   */
  isInHistory(itemId) {
    return this.data.history.some(
      item => (item.id || item.link) === itemId
    );
  }

  /**
   * Contar items en historial
   */
  getHistoryCount() {
    return this.data.history.length;
  }

  // ==================== UTILIDADES ====================

  /**
   * Exportar datos (para backup)
   */
  exportData() {
    return JSON.stringify(this.data, null, 2);
  }

  /**
   * Importar datos (desde backup)
   */
  importData(jsonData) {
    try {
      const imported = JSON.parse(jsonData);
      if (imported.favorites && imported.history) {
        this.data = imported;
        this.save();
        return true;
      }
      return false;
    } catch (e) {
      console.error('Error importing data:', e);
      return false;
    }
  }

  /**
   * Obtener estadísticas
   */
  getStats() {
    return {
      favoritesCount: this.data.favorites.length,
      historyCount: this.data.history.length,
      totalSize: new Blob([JSON.stringify(this.data)]).size
    };
  }
}

// Exportar singleton
export default new BookmarksService();
